import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Box, Button, CircularProgress, DialogActions, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../../lib/date/formatDate';
import { DATE_FORMAT } from '../../../../lib/resolveFields';

interface InstanceDetailFooterProps {
  updatedAt: string | undefined;
  onClose: () => void;
  onSave: () => void;
  saving: boolean;
  /** Hides the save button (ADR-040 D5). */
  readOnly?: boolean;
}

export function InstanceDetailFooter({
  updatedAt,
  onClose,
  onSave,
  saving,
  readOnly = false,
}: InstanceDetailFooterProps) {
  const theme = useTheme();
  const { t } = useTranslation('instances');

  return (
    <DialogActions
      sx={{
        px: 3,
        py: 1.75,
        borderTop: `1px solid ${theme.palette.divider}`,
        background: alpha(theme.palette.primary.main, 0.015),
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <AccessTimeIcon
          sx={{ fontSize: theme.iconSize.xs, opacity: 0.6, color: theme.palette.text.label }}
        />
        <Typography variant="caption" sx={{ color: theme.palette.text.label }}>
          {t('detail.footer.updatedAt')} {updatedAt ? formatDate(updatedAt, DATE_FORMAT) : '—'}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={onClose}
          disabled={saving}
          sx={{
            borderRadius: theme.borderRadius.pill,
            textTransform: 'none',
            color: theme.palette.text.body,
            borderColor: theme.palette.divider,
          }}
        >
          {t(readOnly ? 'detail.footer.close' : 'detail.footer.cancel')}
        </Button>
        {!readOnly && (
          <Button
            variant="contained"
            size="small"
            onClick={e => {
              (e.currentTarget as HTMLElement).blur();
              onSave();
            }}
            disabled={saving}
            aria-busy={saving}
            startIcon={
              saving ? (
                <CircularProgress size={theme.iconSize.xs} sx={{ color: 'inherit' }} />
              ) : undefined
            }
            sx={{
              borderRadius: theme.borderRadius.pill,
              textTransform: 'none',
              background: theme.palette.primary.main,
              '&:hover': { background: theme.palette.primary.main },
              '&:disabled': {
                opacity: 0.55,
                background: theme.palette.primary.main,
                color: theme.palette.common.white,
              },
            }}
          >
            {t('detail.footer.save')}
          </Button>
        )}
      </Box>
    </DialogActions>
  );
}
